/* eslint-disable react-hooks/rules-of-hooks */
import * as Accordion from '@radix-ui/react-accordion'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import classNames from 'classnames'
import React, { useEffect, useState } from 'react'
import { NumericFormat } from 'react-number-format'

interface DrawProps {
  title: string
  data: any
  forceOpen?: boolean
  noContainer?: boolean
}

interface AccordionItemProps {
  children: React.ReactNode
  className?: string
  value: string
}

interface AccordionTriggerProps {
  children: React.ReactNode
  className?: string
  forceOpen?: boolean
}

interface AccordionContentProps {
  children: React.ReactNode
  className?: string
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <Accordion.Item
      className={classNames(
        'mt-px overflow-hidden first:mt-0 focus-within:relative',
        className,
      )}
      {...props}
      ref={forwardedRef}
    >
      {children}
    </Accordion.Item>
  ),
)
AccordionItem.displayName = 'AccordionItem'

const AccordionTrigger = React.forwardRef<
  HTMLDivElement,
  AccordionTriggerProps
>(({ children, className, forceOpen, ...props }, forwardedRef) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleClick = () => {
    setIsOpen(!isOpen)
  }

  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true)
    }
  }, [forceOpen])

  return (
    <Accordion.Header className="flex">
      <Accordion.Trigger
        className={classNames(
          'group text-xl py-8 !leading-snug flex flex-1 cursor-default items-center justify-between bg-white outline-none hover:cursor-pointer data-[state=closed]:border-b-[1px] border-dark-grey',
          className,
        )}
        {...props}
        data-gt-name="componentevent"
        data-gt-action={isOpen ? 'expand' : 'reduce'}
        data-gt-label={children}
        ref={forwardedRef}
        onClick={handleClick}
      >
        {children}
        {!forceOpen && (
          <ChevronDownIcon
            className="w-6 h-6 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-data-[state=open]:rotate-180"
            aria-hidden
          />
        )}
      </Accordion.Trigger>
    </Accordion.Header>
  )
})
AccordionTrigger.displayName = 'AccordionTrigger'

const AccordionContent = React.forwardRef<
  HTMLDivElement,
  AccordionContentProps
>(({ children, className, ...props }, forwardedRef) => (
  <Accordion.Content
    className={classNames(
      'overflow-hidden data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown',
      className,
    )}
    {...props}
    ref={forwardedRef}
  >
    <div className="">{children}</div>
  </Accordion.Content>
))
AccordionContent.displayName = 'AccordionContent'

const Draw = ({ title, data, forceOpen, noContainer }: DrawProps) => {
  if (!data) {
    return null
  }
  const { quote } = data
  const id = title.replaceAll(' ', '-').toLowerCase()
  const [accordionValue, setAccordionValue] = useState<string | null>(
    forceOpen ? 'your-quote' : null,
  )

  useEffect(() => {
    if (forceOpen) {
      setAccordionValue('your-quote')
    } else {
      setAccordionValue(null)
    }
  }, [forceOpen])

  const currency = (value: number) => {
    return (
      <NumericFormat
        value={value}
        displayType="text"
        thousandSeparator
        decimalScale={2}
        fixedDecimalScale={true}
        prefix="£"
      />
    )
  }

  return (
    <Accordion.Root
      className=""
      type="single"
      collapsible={!forceOpen}
      value={accordionValue}
      onValueChange={setAccordionValue}
    >
      <div
        className={`${noContainer ? '' : 'container'}`}
        data-gt-componentname="accordion"
      >
        <AccordionItem value={id}>
          <AccordionTrigger forceOpen={forceOpen}>{title}</AccordionTrigger>
          <AccordionContent>
            <div className="flex justify-between items-center gap-4">
              <div>
                <p className="text-lg pb-1">{quote.textModel}</p>
                <p className="text-sm">{quote.textCarDescBodystyle}</p>
                <p className="text-sm">{quote.textCarDescGrade}</p>
                <p className="text-sm">{quote.textCarDescEngine}</p>
              </div>
              <div>
                <img
                  src={quote.PRINTIMAGESRC}
                  alt={quote.textModel}
                  className="w-full max-w-[300px]"
                />
              </div>
            </div>
            <div className="pt-10">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <div>
                  <div className="flex justify-between gap-4 py-4 border-y border-dark-grey">
                    <dt>
                      1<sup>st</sup> Monthly Payment
                    </dt>
                    <dd className="font-semibold">
                      {currency(quote.FirstPayment)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4 py-4 border-b border-dark-grey">
                    <dt>Next {quote.PAYMENT_NORMAL_NUMBER} Payments</dt>
                    <dd className="font-semibold">
                      {currency(quote.PAYMENT_NORMAL)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4 py-4 border-b border-dark-grey">
                    <dt>Customer Deposit</dt>
                    {quote.DEPOSIT_REQUESTED > 0 ? (
                      <dd className="font-semibold">
                        {currency(quote.DEPOSIT_REQUESTED)}
                      </dd>
                    ) : (
                      <dd className="font-semibold">
                        {currency(quote.Deposit - quote.FDA_AMOUNT)}
                      </dd>
                    )}
                  </div>
                  <div className="flex justify-between gap-4 py-4 border-b border-dark-grey">
                    <dt>Cash Price</dt>
                    {quote.CUST_SAVE_AMOUNT > 0 ? (
                      <dd className="font-semibold">
                        {currency(quote.OtrPrice - quote.CUST_SAVE_AMOUNT)}
                      </dd>
                    ) : (
                      <dd className="font-semibold">
                        {currency(quote.OtrPrice)}
                      </dd>
                    )}
                  </div>
                  {quote.FDA_AMOUNT > 0 && (
                    <div className="flex justify-between gap-4 py-4 border-b border-dark-grey">
                      <dt>Finance Deposit Allowance</dt>
                      <dd className="font-semibold">
                        {currency(quote.FDA_AMOUNT)}
                      </dd>
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex justify-between gap-4 py-4 border-b md:border-y border-dark-grey">
                    <dt>Amount of Credit</dt>
                    <dd className="font-semibold">
                      {currency(quote.AMOUNT_CREDIT)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4 py-4 border-b border-dark-grey">
                    <dt>Guarenteed Future Value / Optional Final Payment</dt>
                    <dd className="font-semibold">
                      {currency(quote.FinalPayment)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4 py-4 border-b border-dark-grey">
                    <dt>Total Amount Payable</dt>
                    <dd className="font-semibold">
                      {currency(quote.TOTAL_AMOUNT_PAYABLE)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4 py-4 border-b border-dark-grey">
                    <dt>Term</dt>
                    <dd className="font-semibold">{quote.Term} months</dd>
                  </div>
                  <div className="flex justify-between gap-4 py-4 border-b border-dark-grey">
                    <dt>Representative</dt>
                    <dd className="font-semibold">{quote.APR}% APR</dd>
                  </div>
                </div>
              </dl>
            </div>
          </AccordionContent>
        </AccordionItem>
      </div>
    </Accordion.Root>
  )
}

export default Draw
